import { type ReactNode } from 'react';
import { TagGroup as AriaTagGroup, type TagGroupProps, TagList, type TagListProps } from 'react-aria-components';
import { FieldError, Label, Text } from '@launchpad-ui/components';
import clsx from 'clsx';

import styles from './TagGroup.module.css';

type Props<T extends object> = Omit<TagGroupProps, 'children'> &
  Pick<TagListProps<T>, 'items' | 'children' | 'renderEmptyState'> & {
    label?: string;
    description?: string;
    errorMessage?: string;
    renderAddTrigger?: (() => ReactNode) | undefined;
    hideSymbol?: boolean;
    renderInlineTrigger?: boolean;
  };

export function TagGroup<T extends object>({
  items,
  label,
  description,
  errorMessage,
  renderEmptyState,
  renderAddTrigger,
  children,
  hideSymbol = false,
  renderInlineTrigger = false,
  ...props
}: Props<T>) {
  return (
    <div
      className={clsx(styles.container, {
        [styles.inlineContainer]: renderInlineTrigger,
      })}
    >
      <AriaTagGroup {...props} className={styles.group}>
        {label && <Label className={styles.label}>{label}</Label>}
        <TagList className={styles.list} items={items} renderEmptyState={renderEmptyState}>
          {children}
        </TagList>
        {description && <Text slot="description">{description}</Text>}
        {errorMessage && <FieldError>{errorMessage}</FieldError>}
      </AriaTagGroup>
      {renderAddTrigger && (
        <>
          {!hideSymbol && (
            <span className={styles.symbol} aria-hidden="true">
              ·
            </span>
          )}
          <div className={styles.add}>{renderAddTrigger()}</div>
        </>
      )}
    </div>
  );
}
